import classNames from 'classnames';
import * as React from 'react';
import { Href } from 'redux-little-router';

import { HoverTooltipContainer } from '../../containers/HoverTooltipContainer/HoverTooltipContainer';
import { Bem } from '../../service/Bem/Bem';
import { TestBem } from '../../service/TestBem';
import { DocCenterTooltip } from '../../typings/doc-center';
import { SortDirection } from '../../typings/grid';
import { GridCellContentOverflow } from '../GridCellContentOverflow/GridCellContentOverflow';
import { HintInfo } from '../HintInfo/HintInfo';
import { Shape2, Shape2Icon, Shape2Theme } from '../Shape2/Shape2';
import { RouteStyledLink } from '../StyledLink/StyledLink';

import './GridHeaderCell.css';

export interface GridHeaderCellProps {
    isSortable: boolean;
    isSortKey: boolean;
    testSortKey?: string;
    isGoalIcon?: boolean;
    sortDirection?: SortDirection;
    sortLinkUrl?: Href;
    title: string;
    tooltipKey?: DocCenterTooltip;
    tooltipText?: string;
    hintInfo?: string;
    onMouseEnter?: (tooltipKey: DocCenterTooltip) => void;
    loadTooltipsFromDoccenter?: boolean;
    isTitleVisible: boolean;
    leftIcon?: JSX.Element;
}

export class GridHeaderCell extends React.PureComponent<GridHeaderCellProps> {
    render() {
        const {
            isSortable,
            sortLinkUrl,
            isSortKey,
            sortDirection,
            tooltipKey,
            tooltipText,
            title,
            hintInfo,
            isTitleVisible,
            leftIcon
        } = this.props;
        let sortingModVal = '';

        if (isSortable && isSortKey) {
            sortingModVal = sortDirection || SortDirection.desc;
        }

        const content = (
            <div
                key={tooltipKey}
                className={Bem.cls({
                    block: 'grid-header-cell',
                    mods: {
                        sorting: sortingModVal,
                        sortable: isSortable,
                        hintable: Boolean(hintInfo)
                    }
                })}
            >
                {leftIcon}
                {isTitleVisible && this.renderGridCellContentOverflow()}
                {this.renderIcons()}
            </div>
        );

        const sortContentContainer =
            isSortable && sortLinkUrl
                ? this.getSortableLink(content, sortLinkUrl)
                : content;

        if (tooltipKey) {
            return (
                <HoverTooltipContainer
                    id={tooltipKey}
                    to={['right', 'bottom', 'left', 'top']}
                    header={title}
                    tooltipText={tooltipText}
                    cls={Bem.cls('grid-header-cell', 'hover-tooltip')}
                >
                    {sortContentContainer}
                </HoverTooltipContainer>
            );
        } else {
            return sortContentContainer;
        }
    }

    private renderGridCellContentOverflow() {
        const { hintInfo, isSortable, sortLinkUrl } = this.props;

        const textContent = this.getTitle();

        if ((!isSortable || !sortLinkUrl) && !hintInfo) {
            return textContent;
        }

        return <GridCellContentOverflow>{textContent}</GridCellContentOverflow>;
    }

    private renderIcons() {
        const { hintInfo, isSortable, sortLinkUrl } = this.props;

        if ((!isSortable || !sortLinkUrl) && !hintInfo) {
            return null;
        }

        return (
            <div className={Bem.cls('grid-header-cell', 'icons')}>
                {this.renderSortIcon()}
                {hintInfo ? (
                    <HintInfo
                        cls={Bem.cls('grid-header-cell', 'hint-info')}
                        theme={Shape2Theme.light}
                        text={hintInfo}
                    />
                ) : null}
            </div>
        );
    }

    private renderSortIcon() {
        const { isSortable, sortLinkUrl } = this.props;

        if (!isSortable || !sortLinkUrl) {
            return null;
        }

        return (
            <span className={Bem.cls('grid-header-cell', 'wrapper-icon-order')}>
                <Shape2
                    cls={Bem.cls('grid-header-cell', 'icon-sort')}
                    icon={Shape2Icon.sort}
                    width={11}
                    height={11}
                />
            </span>
        );
    }

    private getSortableLink(content: React.ReactNode, location: Href) {
        const {
            isSortable,
            isSortKey,
            sortDirection,
            testSortKey
        } = this.props;

        let sortingModVal = '';
        if (isSortable && isSortKey) {
            sortingModVal = sortDirection || SortDirection.desc;
        }

        return (
            <RouteStyledLink
                href={location}
                cls={classNames(
                    Bem.cls('grid-header-cell', 'link', {
                        order: 'direction',
                        sorting: sortingModVal
                    }),
                    TestBem.cls('grid-header-cell', 'link', {
                        key: testSortKey ? Bem.beminize(testSortKey) : false
                    })
                )}
            >
                {content}
            </RouteStyledLink>
        );
    }

    private getTitle() {
        const { title, tooltipText } = this.props;

        return (
            <span
                className={Bem.cls('grid-header-cell', 'title')}
                title={tooltipText ? undefined : title}
            >
                {title}
            </span>
        );
    }
}
